import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useJobs } from '../contexts/JobContext';
import { ArrowLeftIcon, MapPinIcon, CalendarIcon, ClockIcon, AlertCircleIcon, ImageIcon, VideoIcon } from 'lucide-react';
import { motion } from 'framer-motion';
const statusConfig = {
  requested: {
    label: 'Requested',
    color: 'bg-blue-500',
    textColor: 'text-blue-700',
    bgColor: 'bg-blue-50'
  },
  scheduled: {
    label: 'Scheduled',
    color: 'bg-purple-500',
    textColor: 'text-purple-700',
    bgColor: 'bg-purple-50'
  },
  'in-progress': {
    label: 'In Progress',
    color: 'bg-orange-500',
    textColor: 'text-orange-700',
    bgColor: 'bg-orange-50'
  },
  complete: {
    label: 'Complete',
    color: 'bg-green-500',
    textColor: 'text-green-700',
    bgColor: 'bg-green-50'
  },
  paid: {
    label: 'Paid',
    color: 'bg-slate-500',
    textColor: 'text-slate-700',
    bgColor: 'bg-slate-50'
  }
};
const typeLabels = {
  automotive: 'Automotive',
  industrial: 'Industrial',
  residential: 'Residential',
  marine: 'Marine',
  custom: 'Custom'
};
const urgencyConfig = {
  standard: {
    label: 'Standard',
    color: 'text-slate-600',
    bg: 'bg-slate-100'
  },
  urgent: {
    label: 'Urgent',
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  },
  emergency: {
    label: 'Emergency',
    color: 'text-red-600',
    bg: 'bg-red-100'
  }
};
export function JobDetailsPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    getJobById
  } = useJobs();
  const job = id ? getJobById(id) : undefined;
  if (!job) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-528">
        <div className="text-center" data-id="element-529">
          <p className="text-slate-600" data-id="element-530">Job not found</p>
        </div>
      </div>;
  }
  const status = statusConfig[job.status];
  const urgency = urgencyConfig[job.urgency];
  return <div className="min-h-screen bg-slate-50 pb-6" data-id="element-531">
      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 sticky top-0 z-10" data-id="element-532">
        <div className="flex items-center gap-4" data-id="element-533">
          <motion.button whileTap={{
          scale: 0.9
        }} onClick={() => navigate('/')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-534">
            <ArrowLeftIcon className="w-6 h-6" data-id="element-535" />
          </motion.button>
          <div className="flex-1" data-id="element-536">
            <h1 className="text-xl font-bold mb-1" data-id="element-537">{job.title}</h1>
            <p className="text-slate-300 text-sm" data-id="element-538">Job #{job.id}</p>
          </div>
        </div>
      </motion.div>

      <div className="p-6 space-y-6" data-id="element-539">
        {/* Status Banner */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.1
      }} className={`${status.bgColor} border-2 border-${status.color.replace('bg-', '')} rounded-xl p-4`} data-id="element-540">
          <div className="flex items-center justify-between" data-id="element-541">
            <div data-id="element-542">
              <p className="text-sm font-semibold text-slate-600 mb-1" data-id="element-543">
                Current Status
              </p>
              <p className={`text-2xl font-bold ${status.textColor}`} data-id="element-544">
                {status.label}
              </p>
            </div>
            <div className={`w-16 h-16 ${status.color} rounded-2xl flex items-center justify-center`} data-id="element-545">
              <ClockIcon className="w-8 h-8 text-white" data-id="element-546" />
            </div>
          </div>
        </motion.div>

        {/* Job Info */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="bg-white rounded-xl shadow-md p-6 space-y-4" data-id="element-547">
          <div data-id="element-548">
            <p className="text-sm font-semibold text-slate-500 mb-1" data-id="element-549">Type</p>
            <p className="text-lg font-bold text-slate-900" data-id="element-550">
              {typeLabels[job.type]}
            </p>
          </div>

          <div data-id="element-551">
            <p className="text-sm font-semibold text-slate-500 mb-1" data-id="element-552">
              Description
            </p>
            <p className="text-slate-700 leading-relaxed" data-id="element-553">{job.description}</p>
          </div>

          <div className="flex items-center gap-2 pt-2" data-id="element-554">
            <MapPinIcon className="w-5 h-5 text-slate-400" data-id="element-555" />
            <span className="text-slate-700" data-id="element-556">{job.location}</span>
          </div>

          <div className={`inline-flex items-center gap-2 px-4 py-2 ${urgency.bg} rounded-lg`} data-id="element-557">
            <AlertCircleIcon className="w-4 h-4" data-id="element-558" />
            <span className={`font-semibold text-sm ${urgency.color}`} data-id="element-559">
              {urgency.label} Priority
            </span>
          </div>
        </motion.div>

        {/* Schedule Info */}
        {job.scheduledDate && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-xl shadow-md p-6" data-id="element-560">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-561">
              <CalendarIcon className="w-5 h-5" data-id="element-562" />
              Schedule
            </h3>
            <p className="text-slate-700" data-id="element-563">
              {new Date(job.scheduledDate).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
            </p>
          </motion.div>}

        {/* Timeline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="bg-white rounded-xl shadow-md p-6" data-id="element-564">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-565">Timeline</h3>
          <div className="space-y-3" data-id="element-566">
            <div className="flex items-start gap-3" data-id="element-567">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" data-id="element-568"></div>
              <div className="flex-1" data-id="element-569">
                <p className="font-semibold text-slate-900" data-id="element-570">Job Requested</p>
                <p className="text-sm text-slate-500" data-id="element-571">
                  {new Date(job.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            {job.scheduledDate && <div className="flex items-start gap-3" data-id="element-572">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2" data-id="element-573"></div>
                <div className="flex-1" data-id="element-574">
                  <p className="font-semibold text-slate-900" data-id="element-575">Scheduled</p>
                  <p className="text-sm text-slate-500" data-id="element-576">
                    {new Date(job.scheduledDate).toLocaleDateString()}
                  </p>
                </div>
              </div>}
            {job.completedDate && <div className="flex items-start gap-3" data-id="element-577">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2" data-id="element-578"></div>
                <div className="flex-1" data-id="element-579">
                  <p className="font-semibold text-slate-900" data-id="element-580">Completed</p>
                  <p className="text-sm text-slate-500" data-id="element-581">
                    {new Date(job.completedDate).toLocaleDateString()}
                  </p>
                </div>
              </div>}
          </div>
        </motion.div>

        {/* Photos & Videos */}
        {job.media && job.media.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-xl shadow-md p-6" data-id="element-582">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-583">
              <ImageIcon className="w-5 h-5" data-id="element-584" />
              Photos & Videos
            </h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-585">
              {job.media.map((item, index) => <motion.div key={item.id} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.35 + index * 0.05
          }} className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity" data-id="element-586">
                  {item.type === 'image' ? <img src={item.url} alt={`Job media ${index + 1}`} className="w-full h-full object-cover" data-id="element-587" /> : <div className="w-full h-full flex items-center justify-center bg-slate-200" data-id="element-588">
                      <VideoIcon className="w-12 h-12 text-slate-400" data-id="element-589" />
                    </div>}
                  {item.type === 'video' && <div className="absolute inset-0 flex items-center justify-center bg-black/30" data-id="element-590">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center" data-id="element-591">
                        <div className="w-0 h-0 border-l-8 border-l-slate-900 border-t-6 border-t-transparent border-b-6 border-b-transparent ml-1" data-id="element-592"></div>
                      </div>
                    </div>}
                </motion.div>)}
            </div>
          </motion.div>}
      </div>
    </div>;
}